// Deprecated: Avoid using certain functions in this module.
// Migrate to their equivalents in the bridge/ module.
import {
  DEFAULT_AVAILABLE_GOAL_CATEGORIES,
  DEFAULT_GOAL_SUGGESTIONS,
  DEFAULT_ROUTINE_SUGGESTION_PAYLOAD
} from 'assets/data';
import { processAppMetaData } from 'bridge';
import { FLAG, TO_DO_STATUS } from 'enums';
import { LOCAL_STORAGE, LOCAL_STORAGE_FALSE, OK } from 'constants/general';
import {
  AppMetadataType,
  ToDoPlayerTask,
  ToDoTaskAppMetaDataType
} from 'interfaces';
import { t } from 'services/i18n.service';
import { getAppDispatch } from 'store/store-access';
import {
  updateAvailableGoals,
  updateRoutineSuggestionPayload,
  updateSelectedGoals
} from 'store/reducer/routine-suggestions/slice';
import {
  updateFeedback,
  updateIsUserOnboarding
} from 'store/reducer/setting/slice';
import {
  initFocusPlayer,
  updatePlayerClassSessionId,
  updatePlayerMetaResolved,
  updatePlayerSelectedTask
} from 'store/reducer/to-do/slice';
import { fetchCurrentClassSession } from 'store/reducer/class-session/extra';
import { fetchToDosByIds } from 'store/reducer/to-do/extra';
import {
  convertToSeconds,
  formatToDoPlayerTaskTimeConstraints,
  getTodoPlayerTaskDuration,
  safeStringify
} from './helpers';
import { removeLocalStorage, updateLocalStorage } from './helpers/storage';
import { getUniqueGoals } from './routine-suggestion.util';
import { sanitizeInput, uniqueTasksByTitle } from './todo.util';
import { parseIncomingTasks } from 'bridge/parse-incoming-tasks.util';
import { updateCurrentSession } from 'store/reducer/class-session/slice';

export { sendDataToPlatform } from 'bridge/sync-data.bridge';

const filterCompletedTasksFromApi = async (
  tasks: ToDoPlayerTask[]
): Promise<ToDoPlayerTask[]> => {
  const taskIds = tasks.map((task) => task.id).filter(Boolean);
  if (taskIds.length === 0) {
    return tasks;
  }

  const apiTasks = await fetchToDosByIds(taskIds);
  const completedTaskIds = new Set(
    apiTasks
      .filter((task) => task.status === TO_DO_STATUS.COMPLETED)
      .map((task) => task.id)
  );

  return tasks.filter((task) => !completedTaskIds.has(task.id));
};

const persistClassSessionId = (classSessionId: string | null) => {
  getAppDispatch()?.(updatePlayerClassSessionId(classSessionId));
  if (classSessionId) {
    updateLocalStorage(
      LOCAL_STORAGE.TO_DO_PLAYER_CLASS_SESSION_ID,
      classSessionId
    );
    return;
  }
  removeLocalStorage([LOCAL_STORAGE.TO_DO_PLAYER_CLASS_SESSION_ID]);
};

const initializeToDoPlayer = (
  data: ToDoTaskAppMetaDataType,
  tasks: ToDoPlayerTask[]
) => {
  const brainDump = sanitizeInput(data.brain_dump);
  const intention = sanitizeInput(data.intention);

  getAppDispatch()?.(
    initFocusPlayer({
      tasks,
      intention,
      brainDump,
      total_duration: data.total_duration
        ? convertToSeconds(data.total_duration)
        : undefined,
      focusModeEndTime: data.focusmode_endtime,
      classSessionId: null
    })
  );
  persistClassSessionId(null);
  updateLocalStorage(LOCAL_STORAGE.TO_DO_PLAYER_TASKS, safeStringify(tasks));
  processAppMetaData(data);
};

const initializeClassSessionPlayer = async (
  data: ToDoTaskAppMetaDataType,
  classSessionId: string
) => {
  const dispatch = getAppDispatch();
  const brainDump = sanitizeInput(data.brain_dump);
  const intention = sanitizeInput(data.intention);

  persistClassSessionId(classSessionId);
  dispatch?.(updatePlayerSelectedTask(undefined));
  dispatch?.(
    initFocusPlayer({
      tasks: [],
      intention,
      brainDump,
      total_duration: data.total_duration
        ? convertToSeconds(data.total_duration)
        : undefined,
      focusModeEndTime: data.focusmode_endtime,
      classSessionId
    })
  );
  processAppMetaData(data);

  try {
    await dispatch?.(fetchCurrentClassSession()).unwrap();
  } catch {
    dispatch?.(updateCurrentSession(null));
  }
};

export const loadMetaDataForToDoPlayer = () =>
  (window.loadMetaDataForToDoPlayer = async (data: ToDoTaskAppMetaDataType) => {
    updateLocalStorage(LOCAL_STORAGE.IS_BRAIN_DUMP_READY, LOCAL_STORAGE_FALSE);

    const classSessionId =
      typeof data.class_session_id === 'string' &&
      data.class_session_id.trim().length > 0
        ? data.class_session_id.trim()
        : null;

    if (classSessionId) {
      await initializeClassSessionPlayer(data, classSessionId);
    } else {
      const incoming_tasks = uniqueTasksByTitle(parseIncomingTasks(data));
      const tasksWithDuration = incoming_tasks.map((task) => ({
        ...task,
        duration: getTodoPlayerTaskDuration(task.duration)
      }));

      getAppDispatch()?.(
        updateIsUserOnboarding(data?.isOnboarding === true ? true : false)
      );

      const nonCompletedTasks =
        await filterCompletedTasksFromApi(tasksWithDuration);
      const tasks = formatToDoPlayerTaskTimeConstraints(nonCompletedTasks);

      initializeToDoPlayer(data, tasks);
    }

    getAppDispatch()?.(updatePlayerMetaResolved(true));
  });

export const loadAccessTokenForSurvey = () =>
  (window.loadAccessTokenForSurvey = (data: AppMetadataType) => {
    data?.feedback && getAppDispatch()?.(updateFeedback(data.feedback));
    processAppMetaData(data);
    return OK;
  });

export const loadAccessTokenForStats = () => {
  window.loadAccessTokenForStats = (data: AppMetadataType): string => {
    processAppMetaData(data);
    return OK;
  };

  window.refreshStatsPage = () => {
    window.location.reload();
  };
};

export const loadMetaDataForSubscription = () => {
  window.loadMetaDataForSubscription = (data: AppMetadataType) =>
    processAppMetaData(data);
};

export const loadAccessTokenForCourse = () =>
  (window.loadAccessTokenForCourse = (metadata: AppMetadataType) =>
    processAppMetaData(metadata));

export const loadMetaDataForRoutineSuggestion = () =>
  (window.loadMetaDataForRoutineSuggestion = (data: AppMetadataType) => {
    const [RAW_GOAL_BUILD_HEALTHY_HABITS, RAW_GOAL_STAY_FOCUSED_AT_WORK] = [
      'Build healthy habits',
      'Stay focused at work'
    ];
    const [buildHealthyHabits, stayFocusedAtWork] = DEFAULT_GOAL_SUGGESTIONS;
    const defaultGoals = [buildHealthyHabits, stayFocusedAtWork];
    const rawGoals = data?.long_term_goals?.filter(Boolean) || [];

    const normalizedGoals = rawGoals.length
      ? rawGoals.map((goal) => {
          if (goal.includes(RAW_GOAL_BUILD_HEALTHY_HABITS)) {
            return buildHealthyHabits;
          }
          if (goal.includes(RAW_GOAL_STAY_FOCUSED_AT_WORK)) {
            return stayFocusedAtWork;
          }
          return goal;
        })
      : defaultGoals;
    const uniqueGoals = getUniqueGoals(normalizedGoals);
    const actions = [
      updateRoutineSuggestionPayload({
        ...DEFAULT_ROUTINE_SUGGESTION_PAYLOAD,
        user_goals: uniqueGoals
      }),
      updateAvailableGoals({
        ...DEFAULT_AVAILABLE_GOAL_CATEGORIES,
        [t('routine_suggestion.category_other')]: uniqueGoals
      }),
      updateSelectedGoals(uniqueGoals)
    ];

    actions.forEach((action) => getAppDispatch()?.(action));
    return processAppMetaData({ ...data, flags: [FLAG.SIMPLE_MODE] });
  });

export const loadMetaDataForEnrolledCourses = () =>
  (window.loadMetaDataForEnrolledCourses = (metadata: AppMetadataType) =>
    processAppMetaData(metadata));

export const loadMarketPageMetaData = () =>
  (window.loadMarketPageMetaData = (metadata: AppMetadataType) =>
    processAppMetaData(metadata));

export const loadAccessTokenForInspirationPage = () =>
  (window.loadAccessTokenForInspirationPage = (metadata: AppMetadataType) => {
    processAppMetaData(metadata);
    return OK;
  });

export const loadMetaDataFoGetSupport = () =>
  (window.loadMetaDataFoGetSupport = (metadata: AppMetadataType) =>
    processAppMetaData(metadata));

export const loadAccessTokenForChat = () =>
  (window.loadAccessTokenForChat = (metadata: AppMetadataType) => {
    processAppMetaData(metadata);
    return OK;
  });
